import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MemoLookupActions, PromotionDetailsLookupActions } from './discount-service.actions';
import { catchError, map, of, switchMap } from 'rxjs';
import { DiscountService } from '../discount.service';
import { PromotionDetails } from '../../../dtos/response/discount-response/promotion-details';
import { Memo } from '../../../dtos/response/discount-response/memo';
import { Status } from '../../../models/status';
import { Store } from '@ngrx/store';
import { HttpStatusCode } from '@angular/common/http';
import { addJetStreamShortMessage, resetJetStreamShortMessages } from '../../message-service/state/message.actions';
import { MessageKey } from '../../../models/message/message-key';

@Injectable()
export class DiscountCodeEffects {
  private readonly actions$ = inject(Actions);
  private readonly service = inject(DiscountService);

  store = inject(Store);

  looukupMemo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MemoLookupActions.lookup),
      switchMap((action) =>
        this.service.getDiscountCodeMemo(action.discountCode).pipe(
          map((res) => {
            const memo = res as Memo;
            memo.discountCode = action.discountCode;
            return MemoLookupActions.lookupSuccess({ memo: res as Memo });
          }),
          catchError((error) => {
            const {
              status,
              error: { discountCode, message: errorMessage },
            } = error;

            this.store.dispatch(resetJetStreamShortMessages());
            this.handleDiscountCodeMemoLookupErrors(status, action);
            return of(MemoLookupActions.lookupFailure({ error: errorMessage, status: Status.FAILED, discountCode }));
          })
        )
      )
    )
  );

  lookupPromotionDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PromotionDetailsLookupActions.lookup),
      switchMap((action) =>
        this.service.getDiscountCodePromotionDetails(action.campaignId).pipe(
          map((res) => {
            const promotionDetails = res as PromotionDetails;
            promotionDetails.campaignId = action.campaignId;
            return PromotionDetailsLookupActions.lookupSuccess({ promotionDetails });
          }),
          catchError((error) => {
            const {
              status,
              error: { campaignId, message: errorMessage },
            } = error;
            return of(PromotionDetailsLookupActions.lookupFailure({ error: errorMessage, status, campaignId }));
          })
        )
      )
    )
  );

  private handleDiscountCodeMemoLookupErrors(status: any, action: any) {
    let messageKey: MessageKey | null = null;

    switch (status) {
      case HttpStatusCode.NotFound:
      case HttpStatusCode.BadRequest:
        messageKey = MessageKey.DISCOUNT_CODE_LOOKUP_FAILED_NOT_FOUND;
        break;
      case HttpStatusCode.GatewayTimeout:
        messageKey = MessageKey.DISCOUNT_CODE_LOOKUP_FAILED_TIMEOUT;
        break;
      default:
        messageKey = MessageKey.DISCOUNT_CODE_LOOKUP_FAILED_SYSTEM_ERROR;
        break;
    }
    if (messageKey) {
      this.store.dispatch(addJetStreamShortMessage(messageKey, [action.discountCode]));
    }
  }
}
